// src/admin/types/order.ts
export type CurrencyCode = string;
export type DeliverySpeed = "normal" | "express";

export enum OrderStatus {
  Pending = "pending",
  Paid = "paid",
  Waiting = "waiting",
  CurrentlyWorking = "currently_working",
  Finishing = "finishing",
  WaitingForShipping = "waiting_for_shipping",
  Shipped = "shipped",
  Delivered = "delivered",
  WaitingForPickup = "waiting_for_pickup",
  ReadyForPickup = "ready_for_pickup",
  PickedUp = "picked_up",
  Authorized = "authorized",
  PartiallyRefunded = "partially_refunded",
  Refunded = "refunded",
  Cancelled = "cancelled",
}

export enum PaymentStatus {
  Unpaid = "unpaid",
  Pending = "pending",
  Paid = "paid",
  PartiallyRefunded = "partially_refunded",
  Failed = "failed",
  Refunded = "refunded",
  Disputed = "disputed",
}

export type OrderRefundItem = {
  productId?: string;
  name?: string;
  quantity?: number;
  amount?: number;
  reason?: string;
};

export type OrderRefundRecord = {
  refundId?: string;
  amount: number;
  currency: CurrencyCode;
  reason?: string;
  note?: string;
  source?: string;
  processor?: string;
  processorRefundId?: string;
  createdBy?: string;
  refundedAt: string;
  items?: OrderRefundItem[];
};

export type OrderRefundSummary = {
  totalRefunded: number;
  netTotal: number;
  refundCount: number;
  fullyRefunded: boolean;
  lastRefundedAt?: string;
};

export enum FulfillmentStatus {
  Unfulfilled = "unfulfilled",
  PartiallyFulfilled = "partially_fulfilled",
  Fulfilled = "fulfilled",
  Shipped = "shipped",
  Delivered = "delivered",
  Returned = "returned",
}

export type OrderListItem = {
  _id: string;
  orderNumber: string;
  lookupEmail: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  fulfillmentStatus: FulfillmentStatus;
  currency: CurrencyCode;
  totals: {
    currency?: CurrencyCode;
    subtotal?: number;
    discountTotal?: number;
    shippingTotal?: number;
    taxTotal?: number;
    feesTotal?: number;
    grandTotal: number;
  };
  discounts?: {
    total?: number;
    lines?: Array<{
      type?: string;
      label?: string;
      amount?: number;
      code?: string;
      couponId?: string;
      referralRewardId?: string;
      referralRelationshipId?: string;
    }>;
    coupon?: {
      couponId?: string;
      code?: string;
      amount?: number;
    };
    referral?: {
      referralCode?: string;
      referrerAuthUid?: string;
      referrerEmail?: string;
      referredCustomerDiscountAmount?: number;
      referralRelationshipId?: string;
      referralRewardIdUsed?: string;
      referralRewardAmountUsed?: number;
    };
  };
  refunds?: OrderRefundRecord[];
  refundSummary?: OrderRefundSummary;
  deliveryMethod?: "shipping" | "pickup" | string;
  deliverySpeed?: DeliverySpeed;
  createdAt: string;
};

export type PaymentInfo = {
  provider: string; // e.g. "stripe"
  intentId?: string; // Stripe PaymentIntent ID
  chargeId?: string; // Stripe Charge ID
  methodLabel?: string; // e.g. "Visa •••• 4242"
};

export type OrderListResponse = {
  page: number;
  limit: number;
  total: number;
  pages: number;
  items: OrderListItem[];
};

export type OrderDetail = OrderListItem & {
  items: Array<{
    productId: string;
    category: string;
    categoryLabel?: string;
    quantity: number;
    unitPrice: number;
    currency: CurrencyCode;
    snapshot?: any;
    engraving?: { enabled: boolean; text?: string; font?: string };
    remarks?: string;
    selectedOptions?: {
      color?: string;
      chain?: string;
      size?: number | string;
      lengthInch?: number;
      photoCustomization?: any;
      licensePlateCustomization?: any;
      phoneCaseCustomization?: any;
      pendantCustomization?: any;
      cupCustomization?: any;
      licenseCustomization?: any;
      phoneCoverCustomization?: any;
      tshirtCustomization?: any;
      itemSpecs?: any;
      [k: string]: unknown;
    };
  }>;
  customer: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    isGuest: boolean;
  };
  shipping?: {
    methodCode?: string;
    methodLabel?: string;
    carrier?: string;
    trackingNumbers?: string[];
    estimatedDelivery?: string;
    shippedAt?: string;
    deliveredAt?: string;
    deliveredEmailSentAt?: string;
    latestTrackingEvent?: {
      eventType?: string;
      eventTimestamp?: string;
      city?: string;
      state?: string;
      zip?: string;
      country?: string;
    };
    trackingLastSyncedAt?: string;
    trackingLastError?: string;
    trackingDetails?: Array<{
      carrier?: string;
      trackingNumber?: string;
      status?: string;
      statusCategory?: string;
      statusSummary?: string;
      estimatedDelivery?: string;
      deliveredAt?: string;
      latestEvent?: {
        eventType?: string;
        eventTimestamp?: string;
        city?: string;
        state?: string;
        zip?: string;
        country?: string;
      };
    }>;
  };
  notes?: string;
  tags?: string[];

  // 🔽 New payment meta info from BE
  payment?: PaymentInfo;
};

export type ListQuery = {
  page?: number;
  limit?: number;
  sort?: string;
  orderNumber?: string;
  email?: string;
  status?: OrderStatus | "";
  paymentStatus?: PaymentStatus | "";
  fulfillmentStatus?: FulfillmentStatus | "";
  dateFrom?: string; // ISO
  dateTo?: string; // ISO
  minTotal?: number | "";
  maxTotal?: number | "";
  category?: string;
  q?: string;
};

export type OrderImageMaintenanceResponse = {
  ok: boolean;
  orderId?: string;
  scannedOrders?: number;
  updatedOrders?: number;
  deletedOrders?: number;
  removedImageRefs: number;
  deletedFiles: number;
};
